import { EnumDropdown, NumberInput, TextInput, WysiwygInput } from 'framework/form';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import { EXPIRATION_PERIOD_OPTIONS } from 'domain/shared/newVacancy';
import { POSITION_SLOT_WORKER_TYPE_LABELS } from 'domain/shared/positionSlot';
import { PositionFillRequestPositionSlot } from 'domain/shared/positionFillRequest';
import { ENGLISH_LEVEL_SHORT_OPTIONS } from 'domain/shared/core/constants';
import EmployeeInput from 'domain/shared/employee/input/EmployeeInput';
import RecruitmentPoolInput from 'domain/recruiting/recruitmentPool/shared/input/RecruitmentPoolInput';

const FULL_WIDTH = { xs: 12, md: 12, xl: 12 };
const THIRD_WIDTH = { xs: 12, md: 4, xl: 4 };
const TRHREE_QUARTERS_WIDTH = { xs: 12, md: 8, xl: 8 };

const editorConfig = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  '|',
  'bulletedList',
  'numberedList',
  '|',
  'heading',
];

const fieldSx = {
  '& .floating-label-input, & .floating-label-baseInput, & .floating-label-dropdown': { mb: 0 },
};

const RichTextInput = ({ source, disabled }: { source: string; disabled?: boolean }) => (
  <WysiwygInput source={source} label="" editorConfig={editorConfig} height={150} disabled={disabled} />
);

const buildPositionLabel = (positionSlot: PositionFillRequestPositionSlot) => {
  const title = [positionSlot.specialization, positionSlot.technology, positionSlot.competencyLevel]
    .filter(Boolean)
    .join(' ');

  return [
    title,
    positionSlot.offices.join(', '),
    positionSlot.requiredReplacement ? 'Replacement' : undefined,
    POSITION_SLOT_WORKER_TYPE_LABELS[positionSlot.workerType],
  ]
    .filter(Boolean)
    .join(', ');
};

export const getEditPositionFillRequestSections = ({
  isActive,
  canEditRecruitmentPool,
  positionSlot,
  positionDisplayName,
}: {
  isActive: boolean;
  canEditRecruitmentPool: boolean;
  positionSlot: PositionFillRequestPositionSlot;
  positionDisplayName: string;
}): CreateEditFormSection[] => [
  {
    id: 'request-information',
    title: 'Request information',
    fields: [
      {
        id: 'ownerAlias',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <EmployeeInput
            source="ownerAlias"
            label="Owner"
            defaultFilter={{
              'specification:isStaffingCoordinator': { eq: true },
            }}
            withContractors={false}
            isInitialNameLoad
          />
        ),
      },
      {
        id: 'recruitmentPoolId',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: <RecruitmentPoolInput disabled={!canEditRecruitmentPool || !isActive} />,
      },
    ],
  },
  {
    id: 'position-information',
    title: 'Position information',
    fields: [
      {
        id: 'positionSlotId',
        size: TRHREE_QUARTERS_WIDTH,
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="positionSlotId"
            label="Position"
            options={{ [positionSlot.id]: buildPositionLabel(positionSlot) || positionDisplayName }}
            disabled
            isRequired
          />
        ),
      },
    ],
  },
  {
    id: 'main-information',
    title: 'Main information',
    fields: [
      {
        id: 'expirationPeriodInMonth',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="expirationPeriodInMonth"
            label="Expiration period (Month)"
            options={EXPIRATION_PERIOD_OPTIONS}
            placeholder="Select period"
            disabled
          />
        ),
      },
    ],
  },
  {
    id: 'responsibilities',
    title: 'Responsibilities',
    fields: [{ id: 'responsibilities-editor', size: FULL_WIDTH, content: <RichTextInput source="responsibilities" /> }],
  },
  {
    id: 'requirements',
    title: 'Requirements',
    fields: [
      {
        id: 'requiredExperienceInYears',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <NumberInput
            source="requiredExperience"
            label="Required experience"
            slotProps={{ htmlInput: { min: 0 } }}
            isRequired
          />
        ),
      },
      {
        id: 'englishLevel',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="englishLevel"
            label="English level"
            options={ENGLISH_LEVEL_SHORT_OPTIONS}
            placeholder="Select level"
            isRequired
          />
        ),
      },
      { id: 'requirements-editor', size: FULL_WIDTH, content: <RichTextInput source="requirements" /> },
    ],
  },
  {
    id: 'project-information',
    title: 'Project information',
    fields: [
      {
        id: 'projectName',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: <TextInput source="projectName" label="Project name" />,
      },
      { id: 'project-description-editor', size: FULL_WIDTH, content: <RichTextInput source="projectDescription" /> },
    ],
  },
  {
    id: 'notes',
    title: 'Notes',
    fields: [{ id: 'notes-editor', size: FULL_WIDTH, content: <RichTextInput source="notes" /> }],
  },
];
